// Inline "Recognize" affordance. Two entry points, ONE composer + submit path:
//   • manual (Phase 1) — appears next to a colleague's work comment; the human
//     opens it, fills it in, and confirms.
//   • Dana suggestion (Phase 2) — a "✨ Dana noticed a recognition opportunity"
//     card, pre-filled from an OFFERED candidate. Dana SUGGESTS; the human still
//     edits + explicitly confirms (accepting marks the candidate completed).
// Recognition is created in Performance (non-cash, peer-first). The backend
// enforces self-recognition rejection + tenant enablement.
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Award, Sparkles, X } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Textarea } from '@/features/shared/components/ui/textarea';
import { getApiErrorMessage } from '@/lib/apiError';
import { useEntitlements } from '@/features/entitlements/useEntitlements';
import {
  getRecognitionCategories, createRecognition, type RecognitionCategory,
} from '@/services/recognitionService';

interface Props {
  recipientUserId: string;
  recipientName?: string;
  /** Work item this recognition is anchored to (provenance). */
  sourceWorkItemId?: string;
  sourceCommentId?: string;
  /** Dana-suggestion mode: render the ✨ card, pre-fill, and carry the candidate. */
  suggestion?: {
    candidateId: string;
    category?: string;
    title?: string;
    message?: string;
    reason?: string;
  };
  /** Called after a recognition is created (e.g. to drop a dismissed/consumed card). */
  onDone?: () => void;
}

const selStyle =
  'h-9 rounded-md border border-input bg-background px-2 text-sm w-full';

export const RecognizeInline: React.FC<Props> = ({
  recipientUserId, recipientName, sourceWorkItemId, sourceCommentId, suggestion, onDone,
}) => {
  const isSuggestion = !!suggestion;
  const { hasProduct } = useEntitlements(); // fails open for un-provisioned tenants
  const first = (recipientName || 'them').split(' ')[0];
  const [open, setOpen] = useState(isSuggestion); // suggestions render open
  const [done, setDone] = useState(false);
  const [cats, setCats] = useState<RecognitionCategory[]>([]);
  const [category, setCategory] = useState(suggestion?.category || '');
  const [title, setTitle] = useState(suggestion?.title || '');
  const [message, setMessage] = useState(suggestion?.message || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || cats.length) return;
    getRecognitionCategories()
      .then((c) => {
        setCats(c);
        // Only default the category when nothing (incl. a suggestion) preset it.
        setCategory((cur) => cur || (c.length ? c[0].key : ''));
      })
      .catch(() => { /* leave empty; submit still validates server-side */ });
  }, [open, cats.length]);

  const submit = async () => {
    if (!category) { toast.error('Pick a category.'); return; }
    if (!title.trim()) { toast.error('Add a short title.'); return; }
    setSaving(true);
    try {
      await createRecognition({
        recipient_user_id: recipientUserId,
        category_key: category,
        title: title.trim(),
        message: message.trim(),
        relationship_type: 'collaborator',
        source_domain: sourceWorkItemId ? 'work' : 'manual',
        source_entity_type: sourceWorkItemId ? 'work_item' : undefined,
        source_entity_id: sourceWorkItemId,
        source_comment_id: sourceCommentId,
        candidate_id: suggestion?.candidateId,
        ai_suggested: isSuggestion || undefined,
      });
      toast.success(`Recognized ${first} 🎉`);
      setOpen(false);
      setDone(true);
      onDone?.();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not create the recognition.'));
    } finally {
      setSaving(false);
    }
  };

  // Entitlement gate (D-4): recognition is part of the performance product. Fails
  // open for un-provisioned tenants; only a tenant with entitlements but not
  // performance is hidden. All hooks above run first (stable hook order).
  if (!hasProduct('performance')) return null;

  if (done) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
        <Award className="w-3.5 h-3.5" /> Recognized
      </span>
    );
  }

  // Manual, collapsed entry point.
  if (!open && !isSuggestion) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <Award className="w-3.5 h-3.5" /> Recognize {first}
      </button>
    );
  }

  // A dismissed suggestion collapses to nothing (the human declined it).
  if (!open && isSuggestion) return null;

  const cardClass = isSuggestion
    ? 'mt-2 rounded-md border border-amber-300/70 bg-amber-50/60 dark:bg-amber-500/10 dark:border-amber-500/30 p-2.5 space-y-2'
    : 'mt-2 rounded-md border border-border bg-background p-2.5 space-y-2';

  return (
    <div className={cardClass}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-foreground inline-flex items-center gap-1">
          {isSuggestion
            ? <><Sparkles className="w-3.5 h-3.5 text-amber-500" /> Dana noticed a recognition opportunity</>
            : <><Award className="w-3.5 h-3.5 text-amber-500" /> Recognize {recipientName || first}</>}
        </span>
        <button
          type="button"
          onClick={() => { setOpen(false); if (isSuggestion) onDone?.(); }}
          className="text-muted-foreground hover:text-foreground"
          title={isSuggestion ? 'Dismiss suggestion' : 'Close'}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      {isSuggestion && (
        <p className="text-xs text-muted-foreground">
          {suggestion?.reason
            ? suggestion.reason
            : `Recognize ${recipientName || first} for this contribution? You can edit anything before sending.`}
        </p>
      )}
      <select className={selStyle} value={category} onChange={(e) => setCategory(e.target.value)}>
        {cats.length === 0 && <option value={category}>{category || 'Loading categories…'}</option>}
        {cats.map((c) => (
          <option key={c.key} value={c.key}>{c.label}</option>
        ))}
      </select>
      <Input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="e.g. Excellent attention to detail"
      />
      <Textarea
        rows={2}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder={`Why are you recognizing ${first}?`}
      />
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={() => { setOpen(false); if (isSuggestion) onDone?.(); }} disabled={saving}>
          {isSuggestion ? 'Dismiss' : 'Cancel'}
        </Button>
        <Button size="sm" onClick={submit} disabled={saving}>
          {saving ? 'Recognizing…' : isSuggestion ? 'Recognize' : 'Recognize'}
        </Button>
      </div>
    </div>
  );
};

export default RecognizeInline;
